import config from "@Config/index";
import axios from "axios";
import React from "react";
import { BlogPost } from "./data/blogPageReducer";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";

export const getBlogItem = async (blogSlug: string) => {
  try {
    const res = await axios.get(
      `${getBlogCollectionUrl(
        "blog-posts",
      )}?where[slug][equals]=${blogSlug}&where[_status][equals]=published&where[isArchived][equals]=false`,
    );
    console.log("Blog API response:", res.data);
    // The API returns an object with docs array, not a single blog post
    return res.data?.docs?.[0] || res.data;
  } catch (error) {
    console.error("Error fetching blog item data:", error);
    return null;
  }
};

// Helper function to create URL-friendly ID from text
const createHeadingId = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .trim();

const extractTextFromNode = (node: any): string => {
  if (node.type === "text") {
    return node.text || "";
  }
  if (node.children && Array.isArray(node.children)) {
    return node.children.map((child: any) => extractTextFromNode(child)).join("");
  }
  return "";
};

// Helper function to parse CTA Banner HTML and extract props
const parseCTABanner = (
  htmlContent: string,
): {
  heading: string;
  paragraph: string;
  buttonText: string;
  redirectUrl: string;
} | null => {
  try {
    // Extract redirect URL from data-redirect attribute
    const redirectMatch = htmlContent.match(/data-redirect="([^"]*)"/);
    let redirectUrl = redirectMatch ? redirectMatch[1] : "";

    // If no data-redirect attribute, try to extract from script tag
    if (!redirectUrl) {
      const scriptRedirectMatch = htmlContent.match(
        /(?:const|var|let)\s+redirectUrl\s*=\s*["']([^"']+)["']/,
      );
      redirectUrl = scriptRedirectMatch ? scriptRedirectMatch[1] : "";
    }

    // Extract heading text - handle both inline and multiline content
    const headingMatch = htmlContent.match(/<h5[^>]*class="banner_heading"[^>]*>([\s\S]*?)<\/h5>/);
    const heading = headingMatch ? headingMatch[1].trim() : "";

    // Extract paragraph text - handle both inline and multiline content
    const paragraphMatch = htmlContent.match(
      /<p[^>]*class="banner_paragraph"[^>]*>([\s\S]*?)<\/p>/,
    );
    const paragraph = paragraphMatch ? paragraphMatch[1].trim() : "";

    // Extract button text - handle &nbsp; entities and w-button class variations
    const buttonMatch = htmlContent.match(
      /<span[^>]*class="[^"]*button[^"]*"[^>]*>([\s\S]*?)<\/span>/,
    );
    const buttonText = buttonMatch ? buttonMatch[1].replace(/&nbsp;/g, " ").trim() : "Visit Us";

    if (redirectUrl && heading) {
      return { heading, paragraph, buttonText, redirectUrl };
    }

    return null;
  } catch (error) {
    console.error("Error parsing CTA Banner:", error);
    return null;
  }
};

// Helper function to check if HTML content is a CTA banner
const isCTABanner = (htmlContent: string): boolean => {
  // Check for standard CTA banner with data-redirect attribute
  const hasStandardCTABanner =
    htmlContent.includes('class="cta-banner"') &&
    htmlContent.includes("data-redirect=") &&
    htmlContent.includes("banner_heading");

  // Check for banner_container style CTA banner with script-based redirect
  const hasBannerContainerCTA =
    htmlContent.includes('class="banner_container"') &&
    htmlContent.includes("banner_heading") &&
    (htmlContent.includes("redirectUrl") || htmlContent.includes("window.open"));

  return hasStandardCTABanner || hasBannerContainerCTA;
};

// Helper function to check if HTML content is a Download banner
const isDownloadBanner = (htmlContent: string): boolean =>
  htmlContent.includes('class="banner_pdf_container"') &&
  htmlContent.includes("banner_pdf_container_id") &&
  htmlContent.includes("banner_heading");

// Helper function to check if HTML content is a video embed (YouTube, Vimeo, etc.)
const isVideoEmbed = (htmlContent: string): boolean => {
  // Detect iframe video embeds. Accept both the richtext wrapper and plain iframe embeds.
  const hasIframe = htmlContent.includes("<iframe") && htmlContent.includes("</iframe>");
  const hasVideoSource =
    htmlContent.includes("youtube.com/embed/") ||
    htmlContent.includes("vimeo.com/video/") ||
    htmlContent.includes("player.vimeo.com");

  return hasIframe && hasVideoSource;
};

// Helper function to parse video embed HTML and extract props
const parseVideoEmbed = (
  htmlContent: string,
): {
  src: string;
  title: string;
  styles: string;
} | null => {
  try {
    // First try to extract an <iframe ...>...</iframe> block (handles simple iframe embeds)
    const iframeMatch = htmlContent.match(/<iframe[\s\S]*?<\/iframe>/i);
    const iframeHtml = iframeMatch ? iframeMatch[0] : htmlContent;

    // Extract iframe src (first occurrence)
    const srcMatch = iframeHtml.match(/src=["']([^"']+)["']/i);
    const src = srcMatch ? srcMatch[1] : "";

    // Extract iframe title
    const titleMatch = iframeHtml.match(/title=["']([^"']+)["']/i);
    const title = titleMatch ? titleMatch[1] : "Video";

    // Extract any <style>...</style> content (embedded styles)
    const styleTagMatch = htmlContent.match(/<style>([\s\S]*?)<\/style>/i);
    const styleTagContent = styleTagMatch ? styleTagMatch[1].trim() : "";

    // Extract inline style attribute from iframe (e.g. style="display:block; margin:auto")
    const inlineStyleMatch = iframeHtml.match(/style=["']([\s\S]*?)["']/i);
    let inlineStyle = inlineStyleMatch ? inlineStyleMatch[1].trim() : "";

    // Remove any width/height declarations from inline styles to allow responsive sizing
    if (inlineStyle) {
      inlineStyle = inlineStyle
        .split(";")
        .map((s) => s.trim())
        .filter((s) => s && !/^\s*(width|height)\s*:/i.test(s))
        .join("; ");
    }

    // Build consolidated styles to be injected into BlogVideoEmbed
    // Force iframe to be responsive and ignore explicit width/height attributes from the embed
    const responsiveOverride =
      ".videoIframe { width: 100% !important; height: auto !important; max-width: 100%; }";

    const inlineStyleBlock = inlineStyle ? `.videoIframe { ${inlineStyle} }` : "";

    const styles = [styleTagContent, inlineStyleBlock, responsiveOverride]
      .filter(Boolean)
      .join("\n");

    if (src) {
      return { src, title, styles };
    }

    return null;
  } catch (error) {
    console.error("Error parsing video embed:", error);
    return null;
  }
};

// Helper function to parse Download Banner HTML and extract props
const parseDownloadBanner = (
  htmlContent: string,
): {
  heading: string;
  paragraph: string;
  buttonText: string;
  fileUrl: string;
  bannerId: string;
} | null => {
  try {
    // Extract banner ID from id attribute
    const idMatch = htmlContent.match(/id="(banner_pdf_container_id_\d+)"/);
    const bannerId = idMatch ? idMatch[1] : "banner_pdf_container_id_1";

    // Extract file URL from the script
    const fileUrlMatch = htmlContent.match(/fileURL\s*=\s*"([^"]*)"/);
    const fileUrl = fileUrlMatch ? fileUrlMatch[1] : "";

    // Extract heading text - handle both inline and multiline content
    const headingMatch = htmlContent.match(/<h5[^>]*class="banner_heading"[^>]*>([\s\S]*?)<\/h5>/);
    const heading = headingMatch ? headingMatch[1].trim() : "";

    // Extract paragraph text - handle both inline and multiline content
    const paragraphMatch = htmlContent.match(
      /<p[^>]*class="banner_paragraph"[^>]*>([\s\S]*?)<\/p>/,
    );
    const paragraph = paragraphMatch ? paragraphMatch[1].trim() : "";

    // Extract button text
    const buttonMatch = htmlContent.match(
      /<span[^>]*class="[^"]*button[^"]*"[^>]*>([\s\S]*?)<\/span>/,
    );
    const buttonText = buttonMatch ? buttonMatch[1].trim().toLowerCase() : "download";

    if (fileUrl && heading) {
      return { heading, paragraph, buttonText, fileUrl, bannerId };
    }

    return null;
  } catch (error) {
    console.error("Error parsing Download Banner:", error);
    return null;
  }
};

// Helper function to render Lexical JSON as React elements
export const renderLexicalContent = (
  postBody: any,
  componentRegistry?: {
    BlogCTABanner?: React.ComponentType<any>;
    BlogDownloadBanner?: React.ComponentType<any>;
    BlogVideoEmbed?: React.ComponentType<any>;
    Image?: React.ComponentType<any>;
  },
): React.ReactNode[] => {
  if (!postBody?.root?.children) {
    return [];
  }

  const renderNode = (node: any, index: number): React.ReactNode => {
    // Handle upload (image) nodes
    if (node.type === "upload" && node.value) {
      const imageData = node.value;
      const fields = node.fields || {};

      // Determine alt text (customAlt takes precedence over default alt)
      const altText = fields.customAlt || imageData.alt || "";

      // Determine image dimensions
      let width = imageData.width?.toString() || "640";
      let height = imageData.height?.toString() || "427";

      // Determine wrapper styles based on position and sizing
      let wrapperStyle: React.CSSProperties = {};
      let imageStyle: React.CSSProperties = {};

      // Handle custom sizing
      if (fields.sizeType === "custom" && fields.customWidth) {
        width = fields.customWidth;
        // If customWidth is a percentage, set height to auto for aspect ratio
        if (typeof fields.customWidth === "string" && fields.customWidth.trim().endsWith("%")) {
          height = "auto";
          imageStyle = { maxWidth: "100%", height: "auto" };
        } else if (imageData.width && imageData.height) {
          // Only calculate pixel height if customWidth is a pixel value
          const aspectRatio = imageData.height / imageData.width;
          let numericWidth = null;
          if (typeof fields.customWidth === "string" && fields.customWidth.trim().endsWith("px")) {
            numericWidth = parseInt(fields.customWidth, 10);
          } else if (!Number.isNaN(Number(fields.customWidth))) {
            numericWidth = Number(fields.customWidth);
          }
          if (numericWidth !== null && !Number.isNaN(numericWidth)) {
            height = `${Math.round(numericWidth * aspectRatio)}px`;
          } else {
            height = "auto";
          }
          // Ensure max-width is 100% to prevent overflow
          imageStyle = { maxWidth: "100%", height: "auto" };
        } else {
          height = "auto";
          imageStyle = { maxWidth: "100%", height: "auto" };
        }
        // Apply positioning styles for custom size
        switch (fields.position) {
          case "centered":
            wrapperStyle = { display: "flex", justifyContent: "center", margin: "20px 0" };
            break;
          default:
            wrapperStyle = { margin: "20px 0" };
            break;
        }
      } else {
        // For auto sizing (not custom), display with 100% width
        imageStyle = { width: "100%", height: "auto" };
        switch (fields.position) {
          case "centered":
            wrapperStyle = { display: "flex", justifyContent: "center", margin: "20px 0" };
            imageStyle = { width: "100%", height: "auto", maxWidth: "100%" };
            break;
          case "fullWidth":
          case "normal":
          default:
            wrapperStyle = { width: "100%", margin: "20px 0" };
            break;
        }
      }

      // If Image component is provided in registry, use it
      if (componentRegistry?.Image) {
        return React.createElement(
          "div",
          { key: index, style: wrapperStyle, className: "blog-image-wrapper" },
          React.createElement(componentRegistry.Image, {
            src: getBlogImageUrl(imageData.url),
            alt: altText,
            width,
            height,
            style: imageStyle,
          }),
        );
      }

      // Fallback: return null if Image component not provided (to comply with linting)
      // In practice, Image component should always be provided
      return null;
    }

    // Handle text nodes
    if (node.type === "text") {
      const text = node.text || "";

      // Apply formatting based on format bit flags
      if (node.format === 1) {
        // Bold
        return React.createElement("strong", { key: index }, text);
      }
      if (node.format === 2) {
        // Italic
        return React.createElement("em", { key: index }, text);
      }
      if (node.format === 4) {
        // Underline
        return React.createElement("s", { key: index }, text);
      }
      if (node.format === 8) {
        // Strikethrough
        return React.createElement("u", { key: index }, text);
      }

      return text;
    }

    // Handle linebreak nodes
    if (node.type === "linebreak") {
      return React.createElement("br", { key: index });
    }

    // Handle link nodes
    if (node.type === "link") {
      const url = node.fields?.url || "#";
      const newTab = node.fields?.newTab;
      const rel = node.fields?.rel;
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement(
        "a",
        {
          key: index,
          href: url,
          target: newTab ? "_blank" : undefined,
          rel: rel || undefined,
        },
        children,
      );
    }

    // Handle paragraph nodes
    if (node.type === "paragraph") {
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement("p", { key: index }, children);
    }

    // Handle heading nodes with ID for anchor linking
    if (node.type === "heading") {
      const tag = node.tag || "h2";
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      // Extract text content to create ID
      const headingText = extractTextFromNode(node);
      const headingId = createHeadingId(headingText);

      return React.createElement(
        tag,
        {
          key: index,
          id: headingId, // Add ID for anchor linking
        },
        children,
      );
    }

    // Handle list nodes
    if (node.type === "list") {
      const tag = node.listType === "bullet" ? "ul" : "ol";
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement(
        tag,
        {
          key: index,
          start: node.start, // For ordered lists
        },
        children,
      );
    }

    // Handle list item nodes
    if (node.type === "listitem") {
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement("li", { key: index }, children);
    }

    // Handle quote nodes
    if (node.type === "quote") {
      const children = node.children?.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement("blockquote", { key: index }, children);
    }

    // Handle code nodes - RENDER HTML CONTENT DIRECTLY OR AS CTA/DOWNLOAD BANNER/VIDEO EMBED
    // Support both "code" and "custom-code" types with HTML language
    if ((node.type === "code" || node.type === "custom-code") && node.language === "html") {
      // Extract the raw HTML content from code-highlight nodes or directly from node.code
      const htmlContent =
        node.code || node.children?.map((child: any) => child.text || "").join("") || "";

      // Check if this is a CTA banner
      if (isCTABanner(htmlContent)) {
        const ctaBannerProps = parseCTABanner(htmlContent);
        if (ctaBannerProps && componentRegistry?.BlogCTABanner) {
          return React.createElement(componentRegistry.BlogCTABanner, {
            key: index,
            ...ctaBannerProps,
          });
        }
      }

      // Check if this is a Download banner
      if (isDownloadBanner(htmlContent)) {
        const downloadBannerProps = parseDownloadBanner(htmlContent);
        if (downloadBannerProps && componentRegistry?.BlogDownloadBanner) {
          return React.createElement(componentRegistry.BlogDownloadBanner, {
            key: index,
            ...downloadBannerProps,
          });
        }
      }

      // Check if this is a video embed
      if (isVideoEmbed(htmlContent)) {
        const videoEmbedProps = parseVideoEmbed(htmlContent);
        if (videoEmbedProps && componentRegistry?.BlogVideoEmbed) {
          return React.createElement(componentRegistry.BlogVideoEmbed, {
            key: index,
            ...videoEmbedProps,
          });
        }
      }

      // For other HTML content, use dangerouslySetInnerHTML
      return React.createElement("div", {
        key: index,
        dangerouslySetInnerHTML: { __html: htmlContent },
        className: "rendered-html-content",
      });
    }

    // Handle other code blocks (non-HTML) normally
    if (node.type === "code") {
      const codeContent = node.children?.map((child: any) => child.text || "").join("") || "";

      return React.createElement(
        "pre",
        {
          key: index,
          className: `code-block language-${node.language || "text"}`,
        },
        React.createElement("code", {}, codeContent),
      );
    }

    // Recursively handle nodes with children (fallback)
    if (node.children && Array.isArray(node.children)) {
      const children = node.children.map((child: any, childIndex: number) =>
        renderNode(child, childIndex),
      );

      return React.createElement("div", { key: index }, children);
    }

    return null;
  };

  return postBody.root.children
    .map((child: any, index: number) => renderNode(child, index))
    .filter(Boolean);
};

/**
 * Generate table of contents from Lexical content based on tocBasedOn field
 * @param postBody - The Lexical JSON content
 * @param tocBasedOn - Comma-separated heading levels (e.g., "h1,h2" or "h2,h3")
 * @returns Array of TOC items with text, id, level and a single-level number
 */
export const generateTableOfContents = (
  postBody: any,
  tocBasedOn?: string,
): Array<{ text: string; id: string; level: string }> => {
  if (!postBody?.root?.children || !tocBasedOn) {
    return [];
  }

  // Parse tocBasedOn to get allowed heading levels (e.g., ["h1", "h2"])
  const allowedLevels = tocBasedOn
    .split(",")
    .map((level) => level.trim().toLowerCase())
    .filter(Boolean);

  if (allowedLevels.length === 0) {
    return [];
  }

  const tocItems: Array<{ text: string; id: string; level: string; number?: string }> = [];

  // Helper function to extract text from node children
  const extractText = (node: any): string => {
    if (node.type === "text") {
      return node.text || "";
    }
    if (node.children && Array.isArray(node.children)) {
      return node.children.map((child: any) => extractText(child)).join("");
    }
    return "";
  };

  // Helper function to create URL-friendly ID from text
  const createId = (text: string): string =>
    text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .trim();

  // Recursively traverse the content tree and collect headings
  const traverseNodes = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.type === "heading") {
        const tag = (node.tag || "h2").toLowerCase();

        // Only include headings which are in allowedLevels
        if (allowedLevels.includes(tag)) {
          const text = extractText(node);
          if (text) {
            const id = createId(text);
            tocItems.push({ text, id, level: tag });
          }
        }
      }

      // Recursively check children
      if (node.children && Array.isArray(node.children)) {
        traverseNodes(node.children);
      }
    });
  };

  traverseNodes(postBody.root.children);

  return tocItems;
};

const imageUrl = `${config.IMAGE_STATIC_URL}/images`;

export const getBlogPostSchema = (blogData: BlogPost) => [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://amberstudent.com/blog/${blogData?.slug}`,
    },
    headline: blogData?.name,
    description: blogData?.metaDescription,
    image: blogData?.mainImage?.url,
    author: {
      "@type": "Organization",
      name: "amberstudent",
      url: "https://amberstudent.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "amberstudent",
      logo: {
        "@type": "ImageObject",
        url: `${imageUrl}/logo/amber_red_logo_small_1.png`,
      },
    },
    // MMM DD, YYYY
    datePublished: blogData?.createdAt,
    dateModified: blogData?.updatedAt,
  },
];

export const getBlogVideoSchema = (blogData: BlogPost) => [
  {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: blogData?.name,
    description: blogData?.metaDescription,
    thumbnailUrl: [blogData?.videoThumbnailUrl],
    uploadDate: blogData?.videoPublishedDate,
    duration: blogData?.videoDuration,
    contentUrl: blogData?.videoContentUrl,
    embedUrl: blogData?.videoEmbedUrl,
  },
];

export const getBlogFAQSchema = (blogData: BlogPost) => {
  if (!blogData?.faqItems || blogData.faqItems.length === 0) {
    return [];
  }

  const validFaqItems = blogData.faqItems
    .filter((item: any) => item?.question && item?.answerRichText)
    .map((item: any) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: extractTextFromRichText(item.answerRichText),
      },
    }));

  if (validFaqItems.length === 0) {
    return [];
  }

  return [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: validFaqItems,
    },
  ];
};

// Helper function to extract text from rich text editor content
const extractTextFromRichText = (richText: any): string => {
  if (!richText?.root?.children || !Array.isArray(richText.root.children)) {
    return "";
  }

  const extractText = (node: any): string => {
    if (!node) return "";

    if (node.text) {
      return node.text;
    }

    if (node.children && Array.isArray(node.children)) {
      return node.children.map((child: any) => extractText(child)).join(" ");
    }

    return "";
  };

  return richText.root.children
    .map((child: any) => extractText(child))
    .filter((text: string) => text.trim() !== "")
    .join(" ")
    .trim();
};

export const getBlogHowToSchema = (blogData: BlogPost) => {
  if (!blogData?.scriptForHowToSchema) {
    return [];
  }

  const scriptContent = blogData.scriptForHowToSchema;
  const schemaObject = extractSchemaFromScript(scriptContent);
  if (schemaObject) {
    return [schemaObject];
  }

  return [];
};

export const getBlogItemListSchema = (blogData: BlogPost) => {
  if (!blogData?.scriptForItemListSchema) {
    return [];
  }

  const scriptContent = blogData.scriptForItemListSchema;
  const schemaObject = extractSchemaFromScript(scriptContent);
  if (schemaObject) {
    return [schemaObject];
  }

  return [];
};

export const extractSchemaFromScript = (scriptContent: string) => {
  try {
    // First, try to extract JSON from script tags
    const jsonMatch = scriptContent.match(/<script[^>]*>([\s\S]*?)<\/script>/i);

    if (jsonMatch && jsonMatch[1]) {
      const jsonString = jsonMatch[1].trim();
      const schemaObject = JSON.parse(jsonString);
      return schemaObject;
    }

    // If no script tags found, try to parse the content directly as JSON
    const trimmedContent = scriptContent.trim();
    if (trimmedContent) {
      const schemaObject = JSON.parse(trimmedContent);
      return schemaObject;
    }

    return null;
  } catch (error) {
    console.error("Error parsing script:", error);
    return null;
  }
};

export default getBlogItem;
